import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { loginUser } from "./LoginService";
import useForm from "../../hooks/useForm";
import UserContext from "../../Shared/UserContext";
import logo from "../../images/logo.png";
import SnackbarNotification from "../SnackbarNotification";
import EyeIcon from "../../icons/eye.svg";
import EyeOffIcon from "../../icons/eye-off.svg";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { t } = useTranslation();

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const history = useHistory();
  const {
    dispatch,
    state: { status, errorMessage },
  } = useContext(UserContext);

  const { values, onChange, onSubmit } = useForm(
    { username: "", password: "" },
    (formData) => loginUser(dispatch, formData, history)
  );

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!values.username || !values.password) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }
    onSubmit();
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetToken || !newPassword || !confirmPassword) {
      showSnackbar(t('placeholder.empty_fields'), 'error');
      return;
    }

    if (newPassword !== confirmPassword) {
      showSnackbar(t('login.password_mismatch') || 'Les mots de passe ne correspondent pas', 'error');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/users/password/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: resetToken,
          newPassword: newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Password reset failed');
      }

      showSnackbar(t('login.password_reset_success') || 'Mot de passe réinitialisé avec succès', 'success');
      setShowResetModal(false);
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showSnackbar(error.message || 'Erreur lors de la réinitialisation', 'error');
    }
  };

  useEffect(() => {
    if (status === "error") {
      console.error(errorMessage);
    }
  }, [status, errorMessage]);

  return (

    <div className="relative flex items-center justify-center h-screen bg-gray-100">
      <Helmet>
        <title>GrayFox - Log in</title>
      </Helmet>
      {/* Background Blur */}
      <div className="absolute top-0 left-0 w-full h-40 bg-[#6379F5] opacity-20 blur-3xl"></div>

      {/* Background Image in Right Half */}
      <div className="absolute bottom-0 left-0 h-full w-1/2 bg-cover bg-center"
        style={{ backgroundImage: `url(${require('../../images/background.png')})`, backgroundSize: "100%", backgroundPosition: "center" }}>
      </div>
      <div className="relative w-[35%] z-10 text-center">
        <form onSubmit={handleLogin}>
          <img src={logo} alt="Logo" className="block mx-auto w-[250px] mb-8 mt-4" />
          <div className="text-left">
            <h2 className="text-[32px] font-semibold text-[#38485C] ">{t(`login.title`)}</h2>
            <p className="text-[#38485C] font-medium text-[18px] mt-2 mb-10">{t(`login.text`)}.</p>
            <label className="text-[18px] font-semibold text-[#38485C]" htmlFor="username">{t(`login.email`)}</label>
            <input
              type="text"
              id="username"
              name="username"
              value={values.username}
              onChange={onChange}
              placeholder={t(`login.email_placeholder`)}
              className="w-full px-4 py-3 placeholder-[16px] font-semibold placeholder-[#AFB0B2] border border-[#E3E3E3] rounded-lg mt-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="mt-4 text-left">
            <label className="text-[18px] font-semibold text-[#38485C]" htmlFor="password">{t(`login.password`)}</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={values.password}
                onChange={onChange}
                placeholder={t(`login.password`)}
                className={`w-full px-4 py-3 placeholder-[16px] font-semibold placeholder-[#AFB0B2] border border-[#E3E3E3] rounded-lg mt-1 focus:outline-none focus:ring-2 ${status === 'error' ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-indigo-500'}`}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-5 text-gray-500 text-sm"
              >
                <img src={showPassword ? EyeOffIcon : EyeIcon} alt="Toggle password visibility" className="w-5 h-5" />
              </button>
            </div>
          </div>

          {status === "error" && (
            <p className="text-red-500 text-sm mt-2 flex items-center">
              <span className="mr-1">⚠️</span> {t(`errors.login`)}.
            </p>
          )}

        <button
          type="submit"  
          className="w-full mt-10 py-3 bg-[#6A79F8] text-white text-[18px] rounded-[12px] hover:bg-indigo-500 transition font-semibold shadow-md"
        >
          {t(`login.title`)}
        </button>

        {/* Forgot Password Link */}
{/*         <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => setShowResetModal(true)}
            className="text-[#6A79F8] text-[14px] font-semibold hover:underline"
          >
            {t('login.forgot_password') || 'Mot de passe oublié ?'}
          </button>
        </div> */}

        </form>
      </div>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Login;
